import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, PolicyCategory, TakedownResult, TakedownTargetType } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface TakedownInput {
  targetType: TakedownTargetType;
  targetId: string;
  reasonCategory: PolicyCategory;
  reasonNote?: string;
}

export interface UseTakedownResult {
  takedown: (input: TakedownInput) => Promise<TakedownResult>;
  isPending: boolean;
  error: ErrorAlert | null;
}

/**
 * `moderation.takedown` (F69) — moderator/admin only, PUBLIC content only,
 * server-enforced fail-closed (§15). Shared by the share-level "Take down"
 * control (`PublicShareReader`) and the per-comment one (`CommentThread`) —
 * same op, different `targetType`.
 */
export function useTakedown(shareId: string): UseTakedownResult {
  const { wsClient } = useWs();
  const queryClient = useQueryClient();

  const mutation = useMutation<TakedownResult, ErrorAlert, TakedownInput>({
    mutationFn: (input) => requestTyped(wsClient, 'moderation.takedown', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['share', shareId] });
    },
  });

  return {
    takedown: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    error: mutation.isError ? mutation.error : null,
  };
}
